import React, { useState } from "react";
import {
  addMonths,
  eachDayInRange,
  endOfMonth,
  formatDisplayDate,
  parseLocalDate,
  startOfMonth,
  toISODate,
} from "helpers/calendar/calendarDates";
import { getDayInfo } from "helpers/calendar/calendarTerms";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const MonthView = ({
  config,
  autoDraftDates,
  closedDates,
  closureLabels,
  today,
  onSelectDay,
  selectedDate,
}) => {
  const initialMonth = startOfMonth(today);
  const [visibleMonth, setVisibleMonth] = useState(initialMonth);

  const monthStart = startOfMonth(visibleMonth);
  const monthEnd = endOfMonth(visibleMonth);
  const gridStart = new Date(monthStart);
  gridStart.setDate(gridStart.getDate() - gridStart.getDay());
  const gridEnd = new Date(monthEnd);
  gridEnd.setDate(gridEnd.getDate() + (6 - gridEnd.getDay()));

  const days = eachDayInRange(gridStart, gridEnd);

  const monthLabel = visibleMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const yearStart = parseLocalDate(`${config.year}-01-01`);
  const yearEnd = parseLocalDate(`${config.year}-12-31`);

  const canGoPrev = visibleMonth > startOfMonth(yearStart);
  const canGoNext = visibleMonth < startOfMonth(yearEnd);

  return (
    <div className="calendar-month" role="region" aria-label="Month view">
      <div className="calendar-month__nav">
        <button
          type="button"
          className="calendar-month__nav-btn"
          onClick={() => setVisibleMonth((m) => addMonths(m, -1))}
          disabled={!canGoPrev}
          aria-label="Previous month"
        >
          ‹
        </button>
        <h3 className="calendar-month__label">{monthLabel}</h3>
        <button
          type="button"
          className="calendar-month__nav-btn"
          onClick={() => setVisibleMonth((m) => addMonths(m, 1))}
          disabled={!canGoNext}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div className="calendar-month__weekdays" aria-hidden="true">
        {WEEKDAY_LABELS.map((label) => (
          <span key={label} className="calendar-month__weekday">
            {label}
          </span>
        ))}
      </div>

      <div className="calendar-month__grid">
        {days.map((date) => {
          const inMonth = date.getMonth() === visibleMonth.getMonth();
          const info = getDayInfo(
            date,
            config,
            autoDraftDates,
            closedDates,
            closureLabels,
            today
          );
          const iso = toISODate(date);
          const isSelected =
            selectedDate && toISODate(selectedDate) === iso;

          const classNames = [
            "calendar-month__day",
            !inMonth && "calendar-month__day--muted",
            !info.term && inMonth && "calendar-month__day--outside-term",
            info.isClosed && "calendar-month__day--closed",
            info.isAutoDraft && "calendar-month__day--auto-draft",
            info.isToday && "calendar-month__day--today",
            isSelected && "calendar-month__day--selected",
          ]
            .filter(Boolean)
            .join(" ");

          const labelParts = [formatDisplayDate(date)];
          if (info.isClosed) labelParts.push(info.closureLabel || "Closed");
          if (info.isAutoDraft) labelParts.push("Fee auto-draft");

          return (
            <button
              key={iso}
              type="button"
              className={classNames}
              onClick={() => onSelectDay?.(date, info)}
              aria-label={labelParts.join(", ")}
              aria-pressed={isSelected || undefined}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MonthView;
